import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Anchor,
  Button,
  Card,
  Code,
  Group,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import type { UseFormReturnType } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toApiError } from '../apollo/errors';
import { QueryState } from '../components/QueryState';
import { OPENROUTER_MODELS_URL } from '../config';
import type { AdminSettingsInput, AdminSettingsQuery } from '../gql/graphql';
import { AdminSettingsDocument, AdminUpdateSettingsDocument } from '../graphql/operations';
import { useLabels } from '../labels';

type Settings = AdminSettingsQuery['adminSettings'];

/** Which of the cards is waiting on the server. */
type Section = 'openRouter' | 'promptKind' | 'promptScary' | 'termsRu' | 'termsEn';

/** The two prompt fields, which are the two the cards below are built from. */
type PromptField = 'storyPromptKind' | 'storyPromptScary';

/** The two "Terms and Policies" fields (TAL-64), one per app language. */
type TermsField = 'termsRu' | 'termsEn';

type FormValues = {
  openRouterModel: string;
  storyPromptKind: string;
  storyPromptScary: string;
  termsRu: string;
  termsEn: string;
};

/**
 * What decides how a story gets written: who writes it (the OpenRouter model)
 * and what it is asked to write -- one template per story type, because a kind
 * bedtime story and a bedtime fright are not the same instructions. The key
 * that pays for it is not here: it is `OPENROUTER_API_KEY` in the API's
 * environment, and no screen can read or set it.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const labels = useLabels();
  const { data, loading, error } = useQuery(AdminSettingsDocument);

  return (
    <Stack gap="md">
      <Group justify="space-between" gap="sm">
        <Title order={2}>{t('settings.title')}</Title>
        {data && (
          <Text size="xs" c="dimmed">
            {data.adminSettings.updatedAt
              ? t('settings.updatedAt', {
                  date: labels.dateTime(data.adminSettings.updatedAt),
                })
              : t('settings.neverSaved')}
          </Text>
        )}
      </Group>

      <QueryState loading={loading && !data} error={error}>
        {data && <SettingsForm settings={data.adminSettings} />}
      </QueryState>
    </Stack>
  );
}

/**
 * Split from the page so the form is built from settings that already exist:
 * `useForm` reads its initial values once, and a form mounted next to a
 * spinner would keep the empty strings it was born with.
 *
 * Nothing re-seeds it when the props change, either — an operator halfway
 * through rewriting the prompt should not lose it because the other card saved.
 * What each button saved is put back by hand, from the answer.
 */
function SettingsForm({ settings }: { settings: Settings }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<Section | null>(null);
  const [save] = useMutation(AdminUpdateSettingsDocument, {
    // `AdminSettings` carries no id, so the cache cannot tell that the mutation
    // answered with the very object the query holds. Written across by hand,
    // the description below ("saved at") follows the save with no second round
    // trip.
    update(cache, { data }) {
      if (!data) return;

      cache.writeQuery({
        query: AdminSettingsDocument,
        data: { adminSettings: data.adminUpdateSettings },
      });
    },
  });

  const form = useForm<FormValues>({
    // Controlled: the reset button compares what is in the textarea with the
    // built-in template on every keystroke.
    mode: 'controlled',
    initialValues: {
      openRouterModel: settings.openRouterModel,
      storyPromptKind: settings.storyPromptKind,
      storyPromptScary: settings.storyPromptScary,
      // No built-in text either language falls back to, unlike the prompts.
      termsRu: settings.termsRu ?? '',
      termsEn: settings.termsEn ?? '',
    },
  });

  /**
   * One partial update, reported the way every form in this console reports
   * one. The API keys its rules by the input's own field names, so
   * `form.setErrors` lands each message under the input that broke it.
   */
  async function apply(
    section: Section,
    input: AdminSettingsInput,
    message: string,
  ): Promise<Settings | null> {
    setPending(section);
    form.clearErrors();

    try {
      const result = await save({ variables: { input } });

      notifications.show({ color: 'teal', message });

      return result.data?.adminUpdateSettings ?? null;
    } catch (thrown) {
      const failure = toApiError(thrown as ErrorLike);

      if (failure && Object.keys(failure.fields).length > 0) form.setErrors(failure.fields);

      notifications.show({
        color: 'red',
        title: t('common.saveFailed'),
        message: failure?.message ?? '',
      });

      return null;
    } finally {
      setPending(null);
    }
  }

  async function saveOpenRouter() {
    const saved = await apply(
      'openRouter',
      { openRouterModel: form.getValues().openRouterModel.trim() },
      t('common.saved'),
    );

    // Put back from the answer: what was stored is the trimmed value, not
    // whatever is still in the input.
    if (saved) form.setFieldValue('openRouterModel', saved.openRouterModel);
  }

  /** One card's save. The other template is not sent, so it is not touched. */
  async function savePrompt(section: Section, field: PromptField) {
    const saved = await apply(section, { [field]: form.getValues()[field] }, t('common.saved'));

    if (saved) form.setFieldValue(field, saved[field]);
  }

  /** Same shape as `savePrompt`, for the language that was actually edited. */
  async function saveTerms(section: Section, field: TermsField) {
    const saved = await apply(section, { [field]: form.getValues()[field] }, t('common.saved'));

    if (saved) form.setFieldValue(field, saved[field] ?? '');
  }

  return (
    <Stack gap="md">
      <Card withBorder padding="md">
        <Stack gap="md">
          <Text fw={600}>{t('settings.openRouter')}</Text>

          <TextInput
            label={t('settings.model')}
            description={
              <>
                {t('settings.modelDescription')}{' '}
                <Anchor href={OPENROUTER_MODELS_URL} target="_blank" rel="noreferrer" inherit>
                  {t('settings.modelsLink')}
                </Anchor>
              </>
            }
            placeholder={t('settings.modelPlaceholder')}
            {...form.getInputProps('openRouterModel')}
          />

          <Text size="xs" c="dimmed">
            {t('settings.keyInEnv')}
          </Text>

          <Group justify="end">
            <Button loading={pending === 'openRouter'} onClick={() => void saveOpenRouter()}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Card>

      <PromptCard
        form={form}
        field="storyPromptKind"
        title={t('settings.promptKind')}
        description={t('settings.promptKindDescription')}
        defaultText={settings.defaultStoryPromptKind}
        placeholders={settings.promptVariables}
        saving={pending === 'promptKind'}
        onSave={() => void savePrompt('promptKind', 'storyPromptKind')}
      />

      <PromptCard
        form={form}
        field="storyPromptScary"
        title={t('settings.promptScary')}
        description={t('settings.promptScaryDescription')}
        defaultText={settings.defaultStoryPromptScary}
        placeholders={settings.promptVariables}
        saving={pending === 'promptScary'}
        onSave={() => void savePrompt('promptScary', 'storyPromptScary')}
      />

      <TermsCard
        form={form}
        field="termsRu"
        title={t('settings.termsRu')}
        description={t('settings.termsRuDescription')}
        saving={pending === 'termsRu'}
        onSave={() => void saveTerms('termsRu', 'termsRu')}
      />

      <TermsCard
        form={form}
        field="termsEn"
        title={t('settings.termsEn')}
        description={t('settings.termsEnDescription')}
        saving={pending === 'termsEn'}
        onSave={() => void saveTerms('termsEn', 'termsEn')}
      />
    </Stack>
  );
}

/**
 * One template: a box, the placeholder names it may use, a reset that only
 * fills the box, and a save that sends this field and no other.
 *
 * The two are the same card because the difference between them is the text
 * inside, and a second copy of this markup is a second place for the reset
 * button to go stale.
 */
function PromptCard({
  form,
  field,
  title,
  description,
  defaultText,
  placeholders,
  saving,
  onSave,
}: {
  form: UseFormReturnType<FormValues>;
  field: PromptField;
  title: string;
  description: string;
  defaultText: string;
  placeholders: readonly string[];
  saving: boolean;
  onSave: () => void;
}) {
  const { t } = useTranslation();
  const isDefault = form.getValues()[field] === defaultText;

  return (
    <Card withBorder padding="md">
      <Stack gap="md">
        <Text fw={600}>{title}</Text>

        <Textarea
          // Labelled by the card's own heading rather than a second copy of
          // the same words above the box.
          aria-label={title}
          description={description}
          autosize
          // Twelve rather than the eighteen of the single card this was, and
          // capped: `autosize` grows a box to the whole template, and the
          // built-in ones are ~90 lines each — two of those unbounded made the
          // page four and a half thousand pixels tall, with the second card's
          // buttons somewhere below the horizon. Past the cap the box scrolls.
          minRows={12}
          maxRows={20}
          ff="monospace"
          styles={{ input: { fontSize: 'var(--mantine-font-size-sm)' } }}
          {...form.getInputProps(field)}
        />

        <Group gap="xs">
          <Text size="xs" c="dimmed">
            {t('settings.placeholders')}
          </Text>
          {placeholders.map((name) => (
            <Code key={name}>{`{{${name}}}`}</Code>
          ))}
        </Group>

        <Group justify="end">
          {/* Fills the box, nothing more: what the operator saves is still
              theirs to look at first. */}
          <Button
            variant="default"
            disabled={isDefault}
            onClick={() => form.setFieldValue(field, defaultText)}
          >
            {t('settings.resetPrompt')}
          </Button>
          <Button loading={saving} onClick={onSave}>
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * One language of "Terms and Policies" (TAL-64): a box and a save, nothing
 * else. Unlike `PromptCard`, there is no built-in text to reset to and no
 * placeholders to list — a language nobody has written is just an empty box
 * until an operator fills it in.
 */
function TermsCard({
  form,
  field,
  title,
  description,
  saving,
  onSave,
}: {
  form: UseFormReturnType<FormValues>;
  field: TermsField;
  title: string;
  description: string;
  saving: boolean;
  onSave: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Card withBorder padding="md">
      <Stack gap="md">
        <Text fw={600}>{title}</Text>

        <Textarea
          aria-label={title}
          description={description}
          autosize
          minRows={12}
          maxRows={20}
          styles={{ input: { fontSize: 'var(--mantine-font-size-sm)' } }}
          {...form.getInputProps(field)}
        />

        <Group justify="end">
          <Button loading={saving} onClick={onSave}>
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
